import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/shared";
import { Lightbox } from "@/components/Lightbox";
import { Download, Folder, FileText } from "lucide-react";

export default function PublicPhotos() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [lb, setLb] = useState(null);
  const [folder, setFolder] = useState("All");

  useEffect(() => {
    axios.get(`${API}/public/photos/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  const src = (p) => `${API}/public/photos/${token}/file/${p.file_id}`;
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0A0A0B] text-white px-4 py-8 sm:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722]">Swift Signings &amp; Services</div>
          <h1 className="font-head font-black uppercase text-2xl sm:text-3xl tracking-tight">Shared Photos</h1>
        </div>
        {children}
      </div>
    </div>
  );
  if (err) return <Shell><Card className="p-10 text-center text-zinc-400 font-mono">This photo link is invalid or has expired.</Card></Shell>;
  if (!data) return <Shell><div className="font-mono text-zinc-500 animate-pulse text-center py-10">Loading photos…</div></Shell>;

  const folders = [...new Set(data.photos.map((p) => p.folder || "PPW"))];
  const visible = folder === "All" ? data.photos : data.photos.filter((p) => (p.folder || "PPW") === folder);
  return (
    <Shell>
      <Card className="p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="share-header">
        <div>
          <div className="font-head font-black text-lg text-white">{data.job?.address}</div>
          <div className="font-mono text-xs text-zinc-500 mt-0.5">{data.job?.service_type}{data.job?.work_order ? ` · WO ${data.job.work_order}` : ""} · {data.photos.length} photos · link valid until {new Date(data.expires_at).toLocaleDateString()}</div>
          {data.note && <p className="text-sm text-zinc-300 mt-3 border-l-4 border-[#FF5722] pl-3">{data.note}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API}/public/photos/${token}/report`} data-testid="share-report-btn" className="inline-flex items-center gap-2 bg-zinc-950 border-2 border-zinc-700 hover:border-[#FF5722] text-white font-head font-bold uppercase text-xs px-4 py-2.5"><FileText size={15} /> Photo report (PDF)</a>
          <a href={`${API}/public/photos/${token}/zip`} data-testid="share-zip-btn" className="inline-flex items-center gap-2 bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-xs px-4 py-2.5"><Download size={15} /> Download all (ZIP)</a>
        </div>
      </Card>
      {folders.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {["All", ...folders].map((f) => (
            <button key={f} onClick={() => setFolder(f)} className={`flex items-center gap-1.5 px-3 py-1.5 border-2 font-mono text-[11px] uppercase ${folder === f ? "bg-[#FF5722] text-black border-black" : "border-zinc-800 text-zinc-400"}`}><Folder size={12} /> {f}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map((p, i) => (
          <Card key={p.id} className="p-0 overflow-hidden group" data-testid={`shared-photo-${p.id}`}>
            <button onClick={() => setLb(i)} className="aspect-square w-full bg-zinc-950 overflow-hidden"><img src={src(p)} alt={p.caption || "Photo"} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" /></button>
            <div className="p-2 flex items-center justify-between gap-2 border-t border-zinc-800">
              <span className="font-mono text-[10px] text-zinc-500 truncate">{p.folder || "PPW"}{p.caption && p.caption !== "Photo" ? ` · ${p.caption}` : ""}</span>
              <a href={src(p)} download className="text-zinc-500 hover:text-[#FF5722]"><Download size={13} /></a>
            </div>
          </Card>
        ))}
      </div>
      <Lightbox items={visible.map((p) => ({ src: src(p), name: p.caption || "Photo" }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
      <p className="text-center text-zinc-600 font-mono text-xs">Questions about this work? Reply to the email you received or contact Swift Signings &amp; Services.</p>
    </Shell>
  );
}
